import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CircleAlert, Clipboard, ClipboardCheck, Download, FileText, Loader2, RefreshCw, TerminalSquare } from 'lucide-react';
import { ENGINE_EVENT_KIND, ENGINE_EVENT_RESULT, getEngineEvent } from '../../lib/adminApi';
import { apiErrorMessage } from '../../lib/projectApi';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

export const TONE_BADGE = {
  wait: 'bg-primary/15 text-primary hover:bg-primary/15',
  ok: 'bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))] hover:bg-[hsl(var(--wp-context-green))]/15',
  warn: 'bg-[hsl(var(--wp-context-amber))]/20 text-[hsl(var(--wp-context-amber))] hover:bg-[hsl(var(--wp-context-amber))]/20',
  bad: 'bg-destructive/15 text-destructive hover:bg-destructive/15',
  muted: 'bg-muted text-muted-foreground hover:bg-muted',
};

export const eventResult = (ev) => ENGINE_EVENT_RESULT[ev?.result] || { label: ev?.ok ? 'OK' : 'Failed', tone: ev?.ok ? 'ok' : 'bad' };
const exact = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? String(iso || '') : d.toLocaleString(); };
const kb = (bytes) => (bytes == null ? null : bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const ms = (v) => (v == null ? null : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`);
const engineLabel = (e) => (!e ? null : [e.source === 'system' ? 'Host LibreOffice' : e.source === 'runtime' ? 'App runtime' : e.state, e.version].filter(Boolean).join(' · ') || null);

/** Plain-text report of one event (what "Download report" saves and "Copy" copies in full). */
export const eventReportText = (ev) => {
  const res = eventResult(ev);
  const lines = [
    `WannaPublish · conversion engine event`,
    `Result:     ${res.label}${ev.ok ? '' : ' (engine failure)'}`,
    `What:       ${ENGINE_EVENT_KIND[ev.kind] || ev.kind}`,
    `When:       ${ev.at} (${exact(ev.at)})`,
    `Event id:   ${ev.id}`,
  ];
  if (ev.file) lines.push(`File:       ${ev.file}${ev.size_bytes != null ? ` (${kb(ev.size_bytes)})` : ''}`);
  if (ev.user) lines.push(`Uploaded by: ${ev.user}`);
  if (ev.duration_ms != null) lines.push(`Took:       ${ms(ev.duration_ms)}`);
  if (ev.job_id) lines.push(`Reference:  ${String(ev.job_id).slice(0, 8).toUpperCase()} (job ${ev.job_id})`);
  if (ev.code) lines.push(`Code:       ${ev.code}`);
  if (ev.message) lines.push(`Message:    ${ev.message}`);
  if (ev.engine) lines.push(`Engine:     ${engineLabel(ev.engine) || '—'}${ev.engine.soffice ? ` (${ev.engine.soffice})` : ''}`);
  const d = ev.details || {};
  if (Object.keys(d).length) {
    lines.push('', 'Run details:');
    Object.entries(d).forEach(([k, v]) => lines.push(`  ${k}: ${v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}`));
  }
  lines.push('', 'LibreOffice output:', ev.log ? ev.log : (ev.ok && ev.result !== 'rejected' ? '(nothing was logged — the conversion succeeded)' : '(no output was captured)'));
  return lines.join('\n') + '\n';
};

const safeName = (s) => (s || 'event').replace(/[^a-z0-9._-]+/gi, '_').slice(0, 60);

const Row = ({ label, value, mono, testId }) => (value == null || value === '' ? null : (
  <div className="flex gap-3 text-xs" data-testid={testId}>
    <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
    <dd className={`min-w-0 break-words ${mono ? 'font-mono text-[11px]' : ''}`}>{value}</dd>
  </div>
));

/**
 * Owner ▸ Conversion engine — drill-down into one event: file, size, who, duration, code, engine in use, message and the
 * full LibreOffice log, with copy / download. `event` is the row (list projection); the full record is fetched on open.
 * `live` events (an install error of the running process) have no record to fetch and are shown as-is.
 */
export const EngineEventDialog = ({ event, onClose }) => {
  const open = Boolean(event);
  const [full, setFull] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!event) return;
    if (event.id === 'live') { setFull(event); return; }
    setLoading(true); setError('');
    try { setFull(await getEngineEvent(event.id)); } catch (e) { setError(await apiErrorMessage(e, 'The event details could not be loaded.')); } finally { setLoading(false); }
  }, [event]);

  useEffect(() => { setFull(null); setCopied(false); if (event) load(); }, [event, load]);

  const ev = full || event;
  const res = eventResult(ev);
  const report = ev ? eventReportText({ ...ev, log: full?.log }) : '';

  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success(`${what} copied`); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Could not copy', { description: 'Your browser blocked clipboard access.' }); }
  };
  const download = () => {
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `engine-${ev.result || (ev.ok ? 'ok' : 'failed')}-${safeName(ev.file || ev.kind)}-${(ev.id || '').slice(0, 8)}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success('Report downloaded', { description: a.download });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="engine-event-dialog">
        {ev && (
          <>
            <DialogHeader className="px-5 pt-5 pb-3 border-b border-border text-left space-y-1">
              <div className="flex flex-wrap items-center gap-2 pr-6">
                <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${ev.ok ? 'bg-primary/10 text-primary' : 'bg-destructive/15 text-destructive'}`}>{ev.ok ? <FileText className="h-4 w-4" aria-hidden="true" /> : <CircleAlert className="h-4 w-4" aria-hidden="true" />}</span>
                <DialogTitle className="text-base font-semibold" style={heading} data-testid="engine-event-dialog-title">{ENGINE_EVENT_KIND[ev.kind] || ev.kind}{ev.file ? ` · ${ev.file}` : ''}</DialogTitle>
                <Badge className={`${TONE_BADGE[res.tone]} whitespace-nowrap`} data-testid="engine-event-dialog-result">{res.label}</Badge>
              </div>
              <DialogDescription className="text-xs" data-testid="engine-event-dialog-when">{exact(ev.at)}{ev.result === 'rejected' ? ' · the file was turned down — not an engine fault' : ''}</DialogDescription>
            </DialogHeader>

            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <dl className="space-y-1.5" data-testid="engine-event-dialog-facts">
                <Row label="File" value={ev.file ? `${ev.file}${ev.size_bytes != null ? ` · ${kb(ev.size_bytes)}` : ''}` : null} testId="engine-event-dialog-file" />
                <Row label="Uploaded by" value={ev.user} testId="engine-event-dialog-user" />
                <Row label="Took" value={ms(ev.duration_ms)} testId="engine-event-dialog-took" />
                <Row label="Reference" value={ev.job_id ? `${String(ev.job_id).slice(0, 8).toUpperCase()} — the code the user sees under "Import failed"` : null} mono testId="engine-event-dialog-reference" />
                <Row label="Code" value={ev.code} mono testId="engine-event-dialog-code" />
                <Row label="Engine" value={ev.engine ? `${engineLabel(ev.engine) || '—'}${ev.engine.soffice ? ` (${ev.engine.soffice})` : ''}` : null} testId="engine-event-dialog-engine" />
                {ev.details?.exit_code !== undefined && <Row label="Exit code" value={ev.details.exit_code == null ? 'none (timed out)' : String(ev.details.exit_code)} mono testId="engine-event-dialog-exit" />}
                {ev.details?.attempts ? <Row label="Attempts" value={String(ev.details.attempts)} testId="engine-event-dialog-attempts" /> : null}
                {ev.details?.command && <Row label="Command" value={ev.details.command} mono testId="engine-event-dialog-command" />}
              </dl>

              {ev.message && (
                <p className={`rounded-lg border px-3 py-2 text-sm break-words ${ev.ok ? 'border-border bg-muted/40' : 'border-destructive/40 bg-destructive/5 text-foreground'}`} data-testid="engine-event-dialog-message">{ev.message}</p>
              )}

              <section>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                  <h3 className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><TerminalSquare className="h-3.5 w-3.5 text-primary" aria-hidden="true" />LibreOffice output</h3>
                  <div className="flex gap-1.5">
                    <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => copy(full?.log || report, full?.log ? 'Log' : 'Report')} disabled={loading || !full} data-testid="engine-event-copy-log">
                      {copied ? <ClipboardCheck className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> : <Clipboard className="h-3.5 w-3.5 mr-1" aria-hidden="true" />}{full?.log ? 'Copy log' : 'Copy report'}
                    </Button>
                    <Button type="button" size="sm" className="h-8" onClick={download} disabled={loading || !full} data-testid="engine-event-download-report">
                      <Download className="h-3.5 w-3.5 mr-1" aria-hidden="true" />Download report
                    </Button>
                  </div>
                </div>
                {loading ? (
                  <div className="space-y-2" aria-busy="true" data-testid="engine-event-dialog-loading"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-11/12" /><Skeleton className="h-4 w-8/12" /></div>
                ) : error ? (
                  <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs flex flex-wrap items-center justify-between gap-2" role="alert" data-testid="engine-event-dialog-error">
                    <span className="inline-flex items-center gap-1.5 text-destructive"><CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />{error}</span>
                    <Button type="button" size="sm" variant="outline" className="h-7" onClick={load} data-testid="engine-event-dialog-retry"><RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" />Retry</Button>
                  </div>
                ) : full?.log ? (
                  <pre className="rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words max-h-64 overflow-auto" tabIndex={0} data-testid="engine-event-dialog-log">{full.log}</pre>
                ) : (
                  <p className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground" data-testid="engine-event-dialog-no-log">
                    {ev.result === 'rejected' && !ev.details ? 'The file was turned down before LibreOffice ran (format or size check).' : ev.ok ? 'Nothing was logged — the conversion succeeded.' : ev.id === 'live' ? 'This is the running process\u2019s install error; there is no LibreOffice output for it.' : 'No LibreOffice output was captured for this event.'}
                  </p>
                )}
              </section>
            </div>
          </>
        )}
        {!ev && <div className="p-5 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Loading…</div>}
      </DialogContent>
    </Dialog>
  );
};
